// apps/vendor/src/features/dashboard/RecentEarnings.tsx
//
// Last 10 earnings transactions block. Wired to the live wallet ledger
// via GET /api/v1/vendor/wallet/transactions (Wave 10).

import { useTranslation } from 'react-i18next';
import { formatCents } from '@printway/shared/currency';

import { useTransactions } from '../wallet/api.js';

function formatTs(ts: number | null): string {
  if (typeof ts !== 'number') return '—';
  return new Date(ts).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function RecentEarnings(): JSX.Element {
  const { t } = useTranslation('dashboard');
  const txQ = useTransactions({ limit: 10 });
  const rows = txQ.data?.rows ?? [];

  return (
    <section className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('recentEarnings.title')}</h3>

      {txQ.isLoading && rows.length === 0 ? (
        <div className="flex flex-col gap-2 py-2">
          {Array.from({ length: 5 }).map((_v, i) => (
            <div
              key={i}
              className="h-10 animate-pulse rounded-md border border-pw-border bg-pw-surface"
            />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-6 text-center text-sm text-pw-text-muted">
          {t('recentEarnings.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-auto text-sm">
            <thead className="border-b border-pw-border text-xs uppercase tracking-wider text-pw-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">{t('recentEarnings.headerTime')}</th>
                <th className="px-3 py-2 text-left">{t('recentEarnings.headerType')}</th>
                <th className="px-3 py-2 text-right">{t('recentEarnings.headerAmount')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.ledger_id}
                  data-testid={`recent-earning-${r.ledger_id}`}
                  className="border-b border-pw-border last:border-0"
                >
                  <td className="px-3 py-2 text-xs text-pw-text-muted">{formatTs(r.created_at)}</td>
                  <td className="px-3 py-2">{r.type}</td>
                  <td className="px-3 py-2 text-right font-medium text-pw-text">
                    {formatCents(r.amount_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default RecentEarnings;
